import { Calendar, CalendarIcon } from 'components'
import { useState, useRef, useCallback } from 'react'
import { useClickOutside } from 'hooks'
import { Box } from 'theme-ui'
import moment from 'moment'
import { noop } from 'utils'
import { InputWithAdornment } from './Input'
import { STUDENT_INPERSON, STUDENT_DC } from '../../constants'
import { UserType } from 'types'

export type InstructionDateInputProps = {
  disabled?: boolean
  currDate?: string
  selectPastDate?: boolean
  userType?: UserType
  isAdmin?: boolean
  onDateChange?: (date: string) => void
  countryCode?: string
  tabIndex?: number
  teachingOrg?: string
  minimumOveride?: string
  maximumOveride?: string
  error?: boolean
  closeOnSelect?: boolean
}

export const InstructionDateInput = ({
  disabled,
  currDate,
  isAdmin = false,
  userType = undefined,
  onDateChange = noop,
  countryCode,
  teachingOrg,
  minimumOveride,
  maximumOveride,
  tabIndex,
  error,
  closeOnSelect = true,
}: InstructionDateInputProps) => {
  const handleDateChange = (date: moment.Moment) => {
    onDateChange(date.format('YYYY-MM-DD'))
    closeOnSelect && closeCalendar()
  }
  const [openCalendar, setOpenCalendar] = useState<boolean>(false)
  const calendarRef = useRef<HTMLDivElement>(null)
  const closeCalendar = useCallback(() => {
    setOpenCalendar(false)
  }, [setOpenCalendar])
  useClickOutside(calendarRef, closeCalendar)

  let minDate, maxDate
  if (isAdmin) {
    minDate = maxDate = undefined
  } else {
    ;[minDate, maxDate] = getValidDateRange({
      userType,
      countryCode,
      teachingOrg,
    })
    if (minimumOveride) minDate = moment(minimumOveride).format('YYYY-MM-DD')
    if (maximumOveride) maxDate = moment(maximumOveride).format('YYYY-MM-DD')
  }

  return (
    <span ref={calendarRef} data-cy="InstructionDateInput">
      <InputWithAdornment
        id="instructionDatePlaceholder"
        name="instructionDatePlaceholder"
        tabIndex={tabIndex}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
        }}
        value={currDate ? moment(currDate).format('MMM Do, YYYY') : ''}
        placeholder={'Please select a date'}
        disabled={disabled}
        aria-invalid={!!error}
        onClick={() => setOpenCalendar((curState) => !curState)}
        data-testid="Calendar"
        after={<CalendarIcon />}
        readOnly
      />
      <Box
        sx={{
          position: 'absolute',
          zIndex: 999,
          opacity: openCalendar ? 1 : 0,
          visibility: openCalendar ? 'visible' : 'hidden',
          pointerEvents: openCalendar ? 'auto' : 'none',
          transition: '400ms ease-in-out',
        }}
      >
        {openCalendar && (
          <Calendar
            initialDate={currDate}
            onDateChange={handleDateChange}
            minimumDate={minDate}
            maximumDate={maxDate}
          />
        )}
      </Box>
    </span>
  )
}

//Date Validation Calculation stuff
type dateRangeType = {
  min?: string
  max?: string
}
type dateRangeWorkingSet = {
  default: dateRangeType
  [countryCode: string]: any
}

type validDateRangeType = {
  IP: dateRangeWorkingSet
  DC: dateRangeWorkingSet
}

const validDateRangeConfig: validDateRangeType = {
  IP: {
    default: { min: '2021-07-01', max: undefined },
    US: {
      '0': {
        min: '-30',
        max: undefined,
      },
      'da97f43c-79ff-4ceb-8bc9-29d86c540641': {
        min: moment().subtract({ days: 30 }).format('YYYY-MM-DD'),
        max: undefined,
      },
      'ab3f32b4-640e-49e9-88f4-11c20c67cd21': {
        min: moment().subtract({ days: 30 }).format('YYYY-MM-DD'),
        max: undefined,
      },
    },
  },
  DC: {
    default: { min: '-14', max: undefined },
  },
}

/**
 * Processes the config date string into an actual date string or undefined
 * @param configDate the string representing the date to output (either "YYYY-MM-DD" -or- "+/-(Number)")
 * @returns { string } date string in the form "YYYY-MM-DD" or undefined
 *
 * any undefined results in being able to pick any date in that direction (past/future)
 * any number repersents days from today
 * any date strings represent that date in the students country
 *
 * EX: (undefined) => undefined
 * EX: ("2021-09-14") => "2021-09-14"
 * EX: If Today is "2021-10-18"
 *      ("-1") => "2021-10-17"
 *      ("0") => "2021-10-18"
 *      ("-30") => "2021-09-18"
 *
 */
function processConfigDate(configDate: string | undefined) {
  //#noLimit
  if (configDate === undefined) return undefined
  //it's already a date string
  else if (Number.isNaN(Number(configDate))) return configDate
  //configDate is a relative date, create the date
  else {
    const daysFromToday = Number(configDate) // can be negative
    const actualDate = moment()
      .add({ days: daysFromToday })
      .format('YYYY-MM-DD')
    return actualDate
  }
}

type getValidDateRangeInputs = {
  userType?: UserType
  countryCode?: string
  teachingOrg?: string
}

function getValidDateRange({
  userType,
  countryCode = '',
  teachingOrg = '',
}: getValidDateRangeInputs) {
  let workingSet
  if (userType === STUDENT_INPERSON)
    workingSet = validDateRangeConfig.IP as dateRangeWorkingSet
  else if (userType === STUDENT_DC)
    workingSet = validDateRangeConfig.DC as dateRangeWorkingSet
  else workingSet = validDateRangeConfig.DC

  let min = workingSet.default.min
  let max = workingSet.default.max
  if (workingSet[countryCode]) {
    workingSet = workingSet[countryCode]
    if (workingSet[teachingOrg]) {
      min = workingSet[teachingOrg].min
      max = workingSet[teachingOrg].max
    } else if (workingSet.default) {
      min = workingSet.default.min
      max = workingSet.default.max
    }
  }
  return [processConfigDate(min), processConfigDate(max)]
}
